"use client"

import { useEffect, useState, useCallback } from "react"
import { useTranslations } from "next-intl"
import { Loader2, Inbox } from "lucide-react"
import { useListingOffers } from "@/hooks/use-offer"
import { OfferCard } from "./offer-card"
import { cn, formatPrice } from "@/lib/utils"
import type { OfferWithRelations } from "@/lib/offers/offer-types"

interface ListingOffersPanelProps {
  listingId: string
  currentUserId: string
  className?: string
  currency?: string | null
  /**
   * Which secondary-surface dialect to render in: `"sheet"` = alpha tints keyed
   * off the overlay panel, `"page"` = the page's discrete `muted`/`card`
   * surfaces. Both are theme-dependent tokens.
   *
   * Values WERE `"dark" | "light"` and the doc-comment said "forced-dark". Both
   * became untrue when the branch moved off `white/*` literals. Still genuinely
   * two-valued — `ListingOffersTrigger` passes `"sheet"` from its two overlay
   * branches, but the SAME component is also mounted straight onto the listing
   * page by `product-details.tsx` and by the trigger's own `forceInline`
   * desktop early-return, both of which take the `"page"` default.
   * @see .tondocs/ui/HANDOFF_SHEET_LIGHT_THEME_2026_08_01.md
   */
  offerSurfaceVariant?: "page" | "sheet"
}

interface OffersResponse {
  offers: OfferWithRelations[]
  stats: {
    pending: number
    accepted: number
    declined: number
    expired: number
    countered: number
    withdrawn: number
    highest_pending: number | null
    highest_overall: number | null
  }
}

export function ListingOffersPanel({
  listingId,
  currentUserId,
  className,
  currency: currencyProp,
  offerSurfaceVariant = "page",
}: ListingOffersPanelProps) {
  const t = useTranslations()
  const [data, setData] = useState<OffersResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const aggregate = useListingOffers(listingId, {
    initial: data?.stats
      ? {
          pendingCount: data.stats.pending,
          highestPending: data.stats.highest_pending,
        }
      : undefined,
  })

  const fetchOffers = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await fetch(`/api/listings/${encodeURIComponent(listingId)}/offers`, {
        credentials: "include",
      })
      if (!res.ok) {
        setError(`HTTP ${res.status}`)
        setLoading(false)
        return
      }
      const json = await res.json()
      setData(json as OffersResponse)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Network error")
    } finally {
      setLoading(false)
    }
  }, [listingId])

  useEffect(() => {
    void fetchOffers()
  }, [fetchOffers])

  // Refetch when realtime aggregate fires (cheap signal)
  useEffect(() => {
    if (aggregate?.pendingCount == null) return
    void fetchOffers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aggregate?.pendingCount])

  const onSheet = offerSurfaceVariant === "sheet"
  const surfaceMuted = onSheet ? "text-foreground/50" : "text-muted-foreground"
  const cardBorder = onSheet ? "border-foreground/10 bg-foreground/5" : "border-border bg-card"

  if (loading && !data) {
    return (
      <div
        className={cn("rounded-2xl border p-4 flex items-center justify-center", cardBorder)}
      >
        <Loader2 className={cn("h-5 w-5 animate-spin", surfaceMuted)} />
      </div>
    )
  }

  if (error) {
    return (
      <div
        className={cn(
          "rounded-2xl border border-destructive/40 p-4 text-sm",
          // Saturated paint on both sides — deliberately NOT tokenised.
          onSheet ? "bg-red-950/40 text-red-200" : "border-destructive/30 bg-destructive/10 text-destructive"
        )}
      >
        {error}
      </div>
    )
  }

  const offers = data?.offers ?? []
  const pending = offers.filter((o) => o.status === "pending")
  const others = offers.filter((o) => o.status !== "pending").slice(0, 5)
  const currency =
    currencyProp ??
    offers[0]?.listing?.currency ??
    offers[0]?.currency ??
    null
  const highestPendingLabel =
    data?.stats.highest_pending != null
      ? formatPrice(String(data.stats.highest_pending), currency ?? undefined)
      : "—"

  if (offers.length === 0) {
    return (
      <div
        className={cn(
          "rounded-2xl border p-6 flex flex-col items-center text-center gap-2",
          cardBorder,
          className
        )}
      >
        <Inbox className={cn("h-8 w-8", surfaceMuted)} />
        <p className={cn("text-sm", surfaceMuted)}>
          {t("offers.panel.empty", { default: "No offers received yet." })}
        </p>
      </div>
    )
  }

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      <div
        className={cn(
          "rounded-2xl border px-4 py-3 flex items-center gap-4 text-sm",
          cardBorder,
          onSheet && "text-foreground"
        )}
      >
        <Stat
          onSheet={onSheet}
          label={t("offers.panel.pending", { default: "Pending" })}
          value={String(pending.length)}
        />
        <Stat
          onSheet={onSheet}
          label={t("offers.panel.highest", { default: "Highest" })}
          value={highestPendingLabel}
        />
        <Stat
          onSheet={onSheet}
          label={t("offers.panel.declined", { default: "Declined" })}
          value={String(data?.stats.declined ?? 0)}
        />
      </div>

      {pending.length > 0 && (
        <div className="flex flex-col gap-2">
          <h3
            className={cn(
              "text-xs font-semibold uppercase tracking-wide px-1",
              onSheet ? "text-foreground/55" : "text-muted-foreground"
            )}
          >
            {t("offers.panel.activeOffers", { default: "Active offers" })}
          </h3>
          {pending.map((o) => (
            <OfferCard
              key={o.id}
              offer={o}
              currentUserId={currentUserId}
              viewerIsListingSeller
              surfaceVariant={offerSurfaceVariant}
            />
          ))}
        </div>
      )}

      {others.length > 0 && (
        <div className="flex flex-col gap-2">
          <h3
            className={cn(
              "text-xs font-semibold uppercase tracking-wide px-1",
              onSheet ? "text-foreground/55" : "text-muted-foreground"
            )}
          >
            {t("offers.panel.recentActivity", { default: "Recent activity" })}
          </h3>
          {others.map((o) => (
            <OfferCard
              key={o.id}
              offer={o}
              currentUserId={currentUserId}
              viewerIsListingSeller
              surfaceVariant={offerSurfaceVariant}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function Stat({ label, value, onSheet }: { label: string; value: string; onSheet?: boolean }) {
  return (
    <div className="flex flex-col">
      <span
        className={cn(
          "text-[10px] uppercase tracking-wide",
          onSheet ? "text-foreground/50" : "text-muted-foreground"
        )}
      >
        {label}
      </span>
      <span className={cn("text-base font-semibold", onSheet ? "text-foreground" : "")}>{value}</span>
    </div>
  )
}
